import { Button } from "@kopi/ui/components/button";
import { Skeleton } from "@kopi/ui/components/skeleton";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { prefetchOutletRegister, rememberRegisterOutlet } from "@/cashier/prefetch";
import { CashierWorkspace } from "@/cashier/workspace";
import { pickOutlet, readStored, useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { useI18n } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError } from "@/lib/api";
import { catalogueSnapshotQuery, meScopesQuery, offlineLeaseQuery, outletsQuery } from "@/lib/queries";
import { useOfflineBundle } from "@/offline/use-offline-bundle";

export const Route = createFileRoute("/_authed/cashier")({
  staticData: { section: "workspace", crumb: "nav.cashier" },
  // Starts the menu and the offline lease as soon as the outlet is known, alongside the shell's own
  // requests, instead of after the page renders (PRD §5 cold start). Never awaited: the page renders
  // as it always has, and picks up the requests already in flight.
  loader: async ({ context }) => {
    const { queryClient } = context;
    const scopes = await queryClient.ensureQueryData(meScopesQuery);
    const outletId = pickOutlet(scopes, await queryClient.ensureQueryData(outletsQuery), readStored())?.id;
    if (!outletId || !canAt(scopes, "sales.create", outletId)) return;
    rememberRegisterOutlet(outletId);
    prefetchOutletRegister(queryClient, outletId);
  },
  component: CashierPage,
});

function Notice(props: { title: string; children: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <Page width="narrow">
      <PageHeader title={t("cashier.title")} subtitle={props.title} />
      <div className="rounded-xl border border-dashed bg-card p-6 text-muted-foreground text-sm">
        {props.children}
      </div>
    </Page>
  );
}

/** The register (R1-14) at the current outlet: the device's own outlet on a register. */
function CashierPage() {
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { outlet } = useCurrentOutlet();
  const { t } = useI18n();
  const allowed = Boolean(outlet && canAt(scopes, "sales.create", outlet.id));
  const outletId = outlet?.id ?? "";
  const snapshot = useQuery({ ...catalogueSnapshotQuery(outletId), enabled: allowed });
  const lease = useQuery(offlineLeaseQuery(outletId));
  const local = useOfflineBundle(outletId, scopes.device?.enrollmentId ?? "");
  const closed = snapshot.error instanceof ApiRequestError && snapshot.error.status === 404;
  const localSnapshot = local.ready ? local.bundle?.catalogue : undefined;
  const activeSnapshot = snapshot.data ?? (closed ? undefined : localSnapshot);

  useEffect(() => {
    const approved = lease.data?.lease;
    if (!snapshot.data || !approved || approved.status !== "active") return;
    if (approved.catalogueVersion !== snapshot.data.version) return;
    void local.save(approved, snapshot.data);
  }, [lease.data?.lease, local.save, snapshot.data]);

  if (!outlet) return <Notice title={t("outlet.none")}>{t("cashier.noOutletHint")}</Notice>;
  if (!allowed) {
    return (
      <Notice title={t("cashier.notAllowed")}>{t("cashier.notAllowedHint", { outlet: outlet.name })}</Notice>
    );
  }
  if (snapshot.isPending && !activeSnapshot) {
    return (
      <Page>
        <PageHeader title={t("cashier.title")} subtitle={t("cashier.loading")} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-busy="true">
          {["a", "b", "c", "d", "e", "f"].map((key) => (
            <Skeleton key={key} className="h-56 rounded-xl" />
          ))}
        </div>
      </Page>
    );
  }
  if (snapshot.isError) {
    if (activeSnapshot) return <CashierWorkspace outletId={outlet.id} snapshot={activeSnapshot} />;
    return (
      <Notice title={t(closed ? "cashier.outletClosed" : "cashier.menuFailed")}>
        <p>
          {closed ? t("cashier.outletClosedHint", { outlet: outlet.name }) : t("cashier.checkConnection")}
        </p>
        {!closed && (
          <Button type="button" variant="outline" className="mt-4" onClick={() => void snapshot.refetch()}>
            {t("common.retry")}
          </Button>
        )}
      </Notice>
    );
  }
  if (!activeSnapshot) return null;
  return <CashierWorkspace outletId={outlet.id} snapshot={activeSnapshot} />;
}
